/* Shared Customily crossword preview and live-gallery controller.
 * Activates from the provider's configured quote + crossword composition.
 * Owns the verified crossword preview path for every matching Customily composition.
 */
(() => {
  let restoreRender;
  const configuredFields = () => window.HanossCustomilyCapabilities?.getConfiguredFields?.() || {};
  const quoteOption = () => configuredFields().quoteOption || null;
  const quoteInput = () => configuredFields().quote || null;
  const crosswordOptionElement = () => configuredFields().crosswordOption || null;
  const crosswordInput = () => configuredFields().crossword || null;
  const relationshipOption = () => configuredFields().relationshipOption || null;
  const relationshipInput = () => configuredFields().relationship || null;
  const customTextOption = () => configuredFields().customTextOption || null;
  const customTextInput = () => configuredFields().customText || null;
  const quoteChoiceValue = (displayed) => {
    if (!displayed) return '';
    const choice = (window.HanossCustomilyCapabilities?.optionModel?.(quoteOption())?.values || [])
      .find((value) => value?.value === displayed || value?.text === displayed);
    return choice?.value || '';
  };
  const quoteValue = () => quoteInput()?.value?.trim()
    || quoteChoiceValue(quoteOption()?.querySelector('[role="combobox"]')?.textContent?.trim());
  const propertyControlValues = () => [...document.querySelectorAll('.customily_sets-panel .customily_option')]
    .flatMap((option) => window.HanossCustomilyCapabilities?.getPropertyControls?.(option) || [])
    .filter((control) => !['radio', 'checkbox'].includes(control.type) || control.checked)
    .map((control) => `${control.name}\u0001${control.value || ''}`)
    .join('\u0000');
  const isConfiguredPropertyControl = (control) => Boolean(control?.closest?.('.customily_sets-panel .customily_option')
    && control.matches?.('[name^="properties["]'));

  let quoteSelectionFromKeyboard = false;
  document.addEventListener('hanoss:customily-keyboard-selection', () => {
    quoteSelectionFromKeyboard = true;
    queueMicrotask(() => { quoteSelectionFromKeyboard = false; });
  });
  const firstDisconnectedName = (value) => {
    const names = value.trim().split(/\s+/).filter(Boolean);
    if (names.length < 2) return '';
    const connected = new Set([0]);
    let changed = true;
    while (changed) {
      changed = false;
      names.forEach((name, index) => {
        if (connected.has(index)) return;
        const letters = new Set(name.toUpperCase());
        if (Array.from(connected).some((other) => Array.from(names[other].toUpperCase()).some((letter) => letters.has(letter)))) {
          connected.add(index);
          changed = true;
        }
      });
    }
    return names.find((_, index) => !connected.has(index)) || '';
  };

  const syncGalleryMode = () => {
    const active = Boolean(quoteValue());
    document.body.classList.toggle('hn-ui-live-gallery', active);
    requestAnimationFrame(() => {
      const grid = document.querySelector('body.hn-ui-reference .product-information__grid');
      const gallery = grid?.querySelector('media-gallery');
      if (!grid || !gallery) return;
      const details = grid.querySelector('.product-details');
      if (details && !details.dataset.hnUiGallerySizeReady && 'ResizeObserver' in window) {
        details.dataset.hnUiGallerySizeReady = 'true';
        new ResizeObserver(syncGalleryMode).observe(details);
      }
      // Macorner keeps the personalized image pinned through the full form.
      // Re-measure when accordions change the details column's intrinsic height.
      const informationExpanded = Boolean(details?.querySelector('details[open]'));
      const referenceWidthDelta = Math.max(0, 1440 - window.innerWidth);
      const minHeight = active && matchMedia('(min-width: 990px)').matches
        ? `${Math.ceil(relationshipOption()
          ? informationExpanded
            ? Math.max(2487 + referenceWidthDelta * 0.344, (details?.getBoundingClientRect().height || 0) + 99)
            : 1550 + referenceWidthDelta * 0.045
          : informationExpanded
            ? Math.max(2287 + referenceWidthDelta * 0.3, (details?.getBoundingClientRect().height || 0) + 99)
            : 1350)}px`
        : '';
      if (grid.style.minHeight !== minHeight) grid.style.minHeight = minHeight;
    });
  };
  const setCrosswordVisible = (visible) => {
    const option = window.HanossCustomilyCapabilities?.optionModel?.(crosswordOptionElement());
    const imageId = Number(option?.functions?.find((item) => item.type === 'crossword')?.image_id);
    const fabricCanvas = window.engraver?.canvas;
    if (!Number.isFinite(imageId) || !fabricCanvas?.getObjects) return false;
    const image = fabricCanvas.getObjects().find((item) => item.type === 'image-placeholder' && Number(item.id) === imageId);
    if (!image) return false;
    if (image.visible !== visible) {
      image.set('visible', visible);
      fabricCanvas.requestRenderAll();
    }
    return true;
  };
  let frameProbe;
  const hasPaintedFrame = (source) => {
    if (!source?.width || !source?.height) return false;
    try {
      const probe = frameProbe || (frameProbe = document.createElement('canvas'));
      probe.width = probe.height = 16;
      const context = probe.getContext('2d', { willReadFrequently: true });
      context.drawImage(source, 0, 0, 16, 16);
      const pixels = context.getImageData(0, 0, 16, 16).data;
      let visiblePixels = 0;
      const colors = new Set();
      for (let index = 3; index < pixels.length; index += 4) {
        if (pixels[index] > 0) {
          visiblePixels++;
          colors.add((pixels[index - 3] >> 4) + ',' + (pixels[index - 2] >> 4) + ',' + (pixels[index - 1] >> 4));
        }
      }
      return visiblePixels > 16 && colors.size > 1;
    } catch { frameProbe = null; return false; }
  };
  // Accept the provider frame before its modal can close/reset the source.
  // Visibility gates only subsequent delivery, never this verified snapshot.
  const copyAcceptedGalleryFrame = (source, gallery) => {
    if (!source?.width || !source?.height || !gallery) return false;
    try {
      if (gallery.width !== source.width || gallery.height !== source.height) {
        gallery.width = source.width;
        gallery.height = source.height;
      }
      gallery.getContext('2d').drawImage(source, 0, 0);
      gallery.dataset.rendered = 'true';
      return true;
    } catch { return false; }
  };
  const setupLiveGallery = () => {
    const media = document.querySelector('body.hn-ui-reference .product-information__media media-gallery');
    if (!media) return;
    const existing = media.closest('.product-information__media')?.querySelector('.hn-ui-live-canvas');
    if (existing) {
      if (existing.parentElement !== media) media.append(existing);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.className = 'hn-ui-live-canvas';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Live personalized ornament preview');
    media.append(canvas);
    const paint = () => {
      if (!canvas.isConnected) return;
      if (document.body.classList.contains('hn-ui-cart-editor__saved-preview-ready')
        || document.body.classList.contains('hn-ui-cart-editor__saved-preview-pending')) return;
      if (!document.hidden && document.body.classList.contains('hn-ui-live-gallery')) {
        const names = crosswordInput();
        if (!names?.value.trim()) setCrosswordVisible(false);
        const source = document.querySelector('.customily-modal-preview-only #preview-canvas');
        if (source?.width && source?.height
          && (document.body.classList.contains('hn-ui-render-current') || hasPaintedFrame(source))) {
          if (canvas.width !== source.width || canvas.height !== source.height) {
            canvas.width = source.width;
            canvas.height = source.height;
          }
          try {
            canvas.getContext('2d').drawImage(source, 0, 0);
            canvas.dataset.rendered = 'true';
            if (!document.body.classList.contains('hn-ui-gallery-loading')
              && !document.body.classList.contains('hn-ui-render-current')
              && quoteValue()
              && !crosswordInput()?.value
              && !document.body.classList.contains('hn-ui-quote-gallery')) {
              document.body.classList.add('hn-ui-quote-gallery');
            }
          } catch { /* Preserve the last painted frame during provider refresh. */ }
        }
      }
    };
    window.HanossCustomilyPreviewFrames?.register(media, paint);
  };
  let galleryObserver;
  let galleryWatchRoot;
  let galleryWatchFrame;
  const watchLiveGallery = () => {
    const root = document.querySelector('body.hn-ui-reference .product-information__media');
    if (root === galleryWatchRoot) return;
    galleryObserver?.disconnect();
    galleryWatchRoot = root;
    if (!root) return;
    galleryObserver = new MutationObserver(() => {
      if (galleryWatchFrame) return;
      galleryWatchFrame = requestAnimationFrame(() => {
        galleryWatchFrame = null;
        setupLiveGallery();
      });
    });
    galleryObserver.observe(root, { childList: true, subtree: true });
  };

  const setupPreviewConfirmation = () => {
    if (document.body.dataset.hnUiConfirmationReady) return;
    document.body.dataset.hnUiConfirmationReady = 'true';
    let observer;
    let previewButton;
    let renderedKey = '';
    let generationReadyKey = '';
    let lastRenderedCanvas = '';
    let renderPromise;
    let renderQueue = Promise.resolve();
    let activeRender;
    let preGenerateTimer;
    let observedKey = '';
    let userEditedOptions = false;
    let lastOptionEditFrame = '';
    let pendingModalKey = '';
    let modalVerificationTimer;
    let addPreviewTimeout;
    let variantWaitInFlight = false;
    let restoringFocus = false;
    let quoteFrameTimer;
    let initialCartVerificationTimer;
    let cartFieldsResolved = false;
    document.addEventListener('hn-ui-cart-edit-fields-resolved', () => { cartFieldsResolved = true; });
    const showCrosswordError = (name) => {
      const wrapper = document.querySelector('.hn-ui-name-tags');
      const error = wrapper?.querySelector('.hn-ui-name-tags__error');
      const entry = wrapper?.querySelector('.hn-ui-name-tags__entry');
      if (!error) return;
      error.textContent = `Cannot generate puzzle with word "${name}"`;
      error.hidden = false;
      entry?.setAttribute('aria-invalid', 'true');
      wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
    };
    const currentRenderKey = () => `${quoteValue()}\u0002${propertyControlValues()}`;
    const markRenderedKey = (key) => {
      if (currentRenderKey() !== key) return false;
      const source = document.querySelector('.customily-modal-preview-only #preview-canvas');
      const gallery = document.querySelector('.product-information__media .hn-ui-live-canvas');
      if (!gallery || !hasPaintedFrame(source) || !copyAcceptedGalleryFrame(source, gallery)) return false;
      renderedKey = key;
      generationReadyKey = key;
      document.body.classList.remove('hn-ui-quote-gallery');
      setGalleryLoading(false);
      document.body.classList.add('hn-ui-render-current');
      window.HanossCustomilyPreviewFrames?.requestFrame();
      // Stored cart fields can hydrate after this controller first mounts.
      // Re-evaluate the gallery mode when a matching render is accepted so
      // the restored personalized canvas replaces the native product media.
      syncGalleryMode();
      const error = document.querySelector('.hn-ui-name-tags__error');
      if (error) error.hidden = true;
      return true;
    };
    const setGalleryLoading = (loading) => {
      // A decoded saved cart image is already visible while provider controls restore.
      if (document.body.classList.contains('hn-ui-cart-editor__saved-preview-ready')) loading = false;
      document.body.classList.toggle('hn-ui-gallery-loading', loading);
      const gallery = document.querySelector('.product-information__media media-gallery');
      if (loading) gallery?.setAttribute('aria-busy', 'true');
      else gallery?.removeAttribute('aria-busy');
      document.querySelectorAll('.product-form-buttons .customily-preview-button, .product-form-buttons #customily-cart-btn').forEach((button) => {
        button.classList.toggle('hn-ui-option-updating', loading);
        const spinner = button.querySelector(':scope > .hn-ui-option-spinner');
        if (loading && !spinner) {
          const indicator = document.createElement('span');
          indicator.className = 'hn-ui-option-spinner';
          indicator.setAttribute('aria-hidden', 'true');
          button.append(indicator);
        } else if (!loading) spinner?.remove();
      });
    };
    const showQuoteImageWhenReady = (key, before) => {
      clearTimeout(quoteFrameTimer);
      const started = performance.now();
      const expectedQuote = quoteValue();
      let lastFrame = '';
      let renderedMatchingText = false;
      let waitingForLateText = false;
      let lateRender = false;
      const quoteTextMatches = () => {
        const api = window.HanossCustomilyCapabilities;
        if (!api?.getOptionsInstance?.()) return true; // DOM-only fixtures/older providers.
        try {
          const name = quoteInput()?.name?.slice(11, -1);
          const items = name ? api.restoreItems({ [name]: expectedQuote }) : [];
          return items.length > 0 && api.textExpectations(items).length > 0 && api.canvasTextsMatch(items);
        } catch { return false; }
      };
      const check = () => {
        if (quoteValue() !== expectedQuote) return;
        const source = document.querySelector('.customily-modal-preview-only #preview-canvas');
        const gallery = document.querySelector('.product-information__media .hn-ui-live-canvas');
        const matchingText = quoteTextMatches();
        if (matchingText && !renderedMatchingText) {
          // Fabric text may be current before its backing canvas is painted.
          // Flush once, then require two matching pixel samples below.
          window.engraver?.canvas?.renderAll?.();
          renderedMatchingText = true;
          lastFrame = '';
        }
        let frame = '';
        try { if (source?.width && source?.height) frame = source.toDataURL(); } catch { /* Wait for Customily. */ }
        if (matchingText && frame && frame !== before && (frame === lastFrame || lateRender) && hasPaintedFrame(source) && gallery
          && copyAcceptedGalleryFrame(source, gallery)) {
          document.body.classList.add('hn-ui-quote-gallery');
          setGalleryLoading(false);
          window.HanossCustomilyPreviewFrames?.requestFrame();
          return;
        }
        if (performance.now() - started >= 4000) {
          // A slow provider must not make the previous quote look complete.
          // Stop polling; use its existing render/model signal for one late handoff.
          const api = window.HanossCustomilyCapabilities;
          if (!waitingForLateText && api?.waitForState && api?.getOptionsInstance?.()) {
            waitingForLateText = true;
            api.waitForState(() => quoteValue() !== expectedQuote || quoteTextMatches(), { timeout: 12000 })
              .then(() => {
                if (quoteValue() !== expectedQuote) return;
                lateRender = true;
                renderedMatchingText = false;
                check();
              }).catch(() => { /* Keep loading instead of confirming stale artwork. */ });
          }
          return;
        }
        lastFrame = frame;
        quoteFrameTimer = setTimeout(check, 75);
      };
      quoteFrameTimer = setTimeout(check, 75);
    };
    const invalidCrosswordName = () => firstDisconnectedName(
      crosswordInput()?.value || '',
    );
    const renderCurrentCrossword = async (create) => {
      create.click();
      return Promise.resolve(renderPromise);
    };
    const showRenderError = () => {
      const error = document.querySelector('.hn-ui-name-tags__error');
      if (!error) return;
      error.textContent = 'Could not update preview. Please try again.';
      error.hidden = false;
    };
    const showMissingPersonalization = (missingQuote, missingNames) => {
      const mark = (option, selector) => {
        const alert = option?.querySelector('.hn-ui-validation-message');
        const control = option?.querySelector(selector);
        if (option) option.dataset.hnUiManualRequired = 'true';
        option?.classList.add('hn-ui-invalid');
        if (alert) alert.hidden = false;
        control?.setAttribute('aria-invalid', 'true');
        if (alert) control?.setAttribute('aria-describedby', alert.id);
        return control;
      };
      const first = missingQuote ? mark(quoteOption(), '.csh-dropdown-input-container') : null;
      const names = missingNames ? mark(crosswordOptionElement(), '.hn-ui-name-tags__entry') : null;
      (first || names)?.focus();
    };
    const setPreviewBusy = (button, busy) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
      let spinner = button.querySelector('.hn-ui-preview-spinner');
      if (busy && !spinner) {
        spinner = document.createElement('span');
        spinner.className = 'hn-ui-preview-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        button.append(spinner);
      } else if (!busy) {
        spinner?.remove();
      }
      const label = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (label) {
        if (busy) {
          button.dataset.hnUiPreviewLabel = label.textContent;
          label.textContent = 'Preparing preview…';
        } else if (button.dataset.hnUiPreviewLabel) {
          label.textContent = button.dataset.hnUiPreviewLabel;
          delete button.dataset.hnUiPreviewLabel;
        }
      }
      const mobileProxy = document.querySelector('.hn-ui-mobile-preview-proxy');
      if (mobileProxy) {
        mobileProxy.disabled = busy;
        mobileProxy.setAttribute('aria-busy', String(busy));
        mobileProxy.textContent = busy ? 'Preparing preview…' : 'Preview';
      }
    };
    const setAddPreviewBusy = (busy) => {
      const button = document.querySelector('body.hn-ui-reference .customily-preview-button');
      if (!button) return;
      clearTimeout(addPreviewTimeout);
      if (busy && button.getAttribute('aria-busy') !== 'true') setPreviewBusy(button, true);
      if (!busy && button.getAttribute('aria-busy') === 'true') setPreviewBusy(button, false);
      if (busy) addPreviewTimeout = setTimeout(() => setAddPreviewBusy(false), 10000);
    };
    const crosswordOption = () => window.HanossCustomilyCapabilities?.optionModel?.(crosswordOptionElement());
    const crosswordValueMatches = (expectedValue) => crosswordInput()?.value === expectedValue;
    const crosswordGenerationSettled = (expectedValue) => {
      const option = crosswordOption();
      if (!option) return crosswordValueMatches(expectedValue);
      return option?.lastGeneratedValue === expectedValue && option.loadingFunction === false;
    };
    const crosswordGenerationReady = (expectedValue) => {
      const option = crosswordOption();
      // Customily no longer always exposes Vue's private __vue__ property.
      // The canvas check below still verifies the image before it is used.
      if (!option) {
        // A stored cart edit already has an authoritative saved image. Do not
        // promote Customily's hidden canvas while its model is still hydrating;
        // mobile Safari can expose a sized but blank or stale canvas here.
        if (document.body.classList.contains('hn-ui-cart-editor__saved-preview-pending')
          || document.body.classList.contains('hn-ui-cart-editor__saved-preview-ready')) return false;
        return crosswordValueMatches(expectedValue);
      }
      return option?.value === expectedValue
        && option.generatedFunction === true
        && option.lastGeneratedValue === expectedValue
        && option.loadingFunction === false
        && option.errorGeneratingFunction === false;
    };
    const verifyInitialCartEdit = () => {
      if (!document.body.classList.contains('hn-ui-cart-editor')) return;
      let intent;
      try { intent = JSON.parse(sessionStorage.getItem('hn-hng04-cart-edit-intent') || 'null'); } catch { return; }
      const url = new URL(location.href);
      if (!intent || intent.path !== url.pathname || intent.search !== url.search) return;
      const stored = !intent.legacy
        && url.searchParams.get('isStoredCustomization') === '1'
        && Boolean(url.searchParams.get('customizationId'))
        && Boolean(url.searchParams.get('templateId'))
        && url.searchParams.get('variant') === String(intent.originalVariantId);
      const clean = intent.legacy && !url.searchParams.has('customizationId');
      if (!stored && !clean) return;
      const quote = quoteValue();
      const relationship = relationshipInput()?.value;
      const names = crosswordInput()?.value;
      const customText = customTextInput()?.value || '';
      if (quote !== intent.quote || relationship !== intent.relationship
        || names !== intent.names || customText !== (intent.customText || '')
        || !crosswordGenerationReady(names)) return;
      if (Number(document.querySelector('product-form-component input[name="id"]')?.value) !== Number(intent.originalVariantId)) return;
      // The saved cart preview is the authoritative initial artwork. Keep it
      // visible until a shopper action produces and verifies a fresh canvas.
      if (document.body.classList.contains('hn-ui-cart-editor__saved-preview-ready')
        || document.body.classList.contains('hn-ui-cart-editor__saved-preview-pending')) return;
      markRenderedKey(currentRenderKey());
    };
    const scheduleInitialCartEditVerification = () => {
      clearTimeout(initialCartVerificationTimer);
      const started = performance.now();
      let readySince = 0;
      const check = () => {
        verifyInitialCartEdit();
        syncGalleryMode();
        if (!document.body.classList.contains('hn-ui-cart-editor')) return;
        if (document.body.classList.contains('hn-ui-render-current')) {
          readySince ||= performance.now();
          if (performance.now() - readySince >= 600) return;
        } else {
          readySince = 0;
        }
        if (performance.now() - started < 30000) {
          initialCartVerificationTimer = setTimeout(check, 150);
        }
      };
      check();
    };
    document.addEventListener('hn-ui-cart-editor-verify-initial', scheduleInitialCartEditVerification);
    document.addEventListener('hn-ui-cart-edit-fields-resolved', scheduleInitialCartEditVerification);
    document.addEventListener('hanoss:customily-capabilities', scheduleInitialCartEditVerification);
    // Provider fields, the stored response and this controller mount in an
    // unpredictable order. Keep a bounded readiness check active so any lost
    // one-shot event cannot leave a valid restored image permanently busy.
    queueMicrotask(scheduleInitialCartEditVerification);
    const waitForCrosswordGeneration = (expectedValue, key) => new Promise((resolve) => {
      const started = performance.now();
      const check = () => {
        if (crosswordGenerationReady(expectedValue)) resolve(true);
        else if (key && currentRenderKey() !== key && crosswordGenerationSettled(expectedValue)) resolve(false);
        else if (performance.now() - started < 8000) setTimeout(check, 75);
        else resolve(false);
      };
      check();
    });
    // Reuse a provider-generated design instead of asking Create to generate it again.
    // The dispatcher flushes pending artwork changes; no export/upload is started.
    const acceptGeneratedFrame = async (key, expectedNames) => {
      const api = window.HanossCustomilyCapabilities;
      const dispatcher = api?.getOptionsInstance?.()?.engraverDispatcher;
      if (!crosswordOption() || !crosswordGenerationReady(expectedNames)
        || currentRenderKey() !== key || typeof dispatcher?.commit !== 'function') return false;
      const items = api.restoreItems(Object.fromEntries(
        [...document.querySelectorAll('.customily_sets-panel [name^="properties["]')]
          .filter((control) => !['radio', 'checkbox'].includes(control.type) || control.checked)
          .map((control) => [control.name.slice(11, -1), control.value])));
      await dispatcher.commit();
      window.engraver?.canvas?.renderAll();
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (currentRenderKey() !== key || !crosswordGenerationReady(expectedNames)
        || !api.canvasTextsMatch(items)) return false;
      const source = document.querySelector('.customily-modal-preview-only #preview-canvas');
      if (!hasPaintedFrame(source)) return false;
      let frame;
      try { frame = source.toDataURL(); } catch { return false; }
      // Matching names alone cannot validate a changed quote/relationship.
      if (userEditedOptions && (!lastOptionEditFrame || frame === lastOptionEditFrame)) return false;
      if (!markRenderedKey(key)) return false;
      lastRenderedCanvas = frame;
      return true;
    };
    const requestCurrentRender = (key) => {
      if (activeRender?.key === key) return activeRender.promise;
      const promise = renderQueue.catch(() => {}).then(async () => {
        if (currentRenderKey() !== key) return false;
        const names = crosswordInput();
        const create = document.querySelector('body.hn-ui-reference .customily_sets-panel .customily-download-button');
        const expectedNames = names?.value || '';
        if (!expectedNames.trim() || !create || create.disabled || invalidCrosswordName()) return false;
        if (renderedKey === key && crosswordGenerationReady(expectedNames)) return true;
        if (crosswordOption()?.loadingFunction === true) {
          await waitForCrosswordGeneration(expectedNames, key);
          if (currentRenderKey() !== key) return false;
        }
        if (await acceptGeneratedFrame(key, expectedNames)) return true;
        const sourceCanvas = document.querySelector('.customily-modal-preview-only #preview-canvas');
        const sourceReady = Boolean(sourceCanvas?.width && sourceCanvas?.height);
        if (!sourceReady && crosswordGenerationReady(expectedNames)) {
          // Closing Preview resets this canvas to 0×0. Repeating Create for the
          // same names cannot paint it here and may dismiss the next modal.
          generationReadyKey = key;
          return false;
        }
        const rendered = sourceReady ? await renderCurrentCrossword(create) : (create.click(), false);
        const generated = await waitForCrosswordGeneration(expectedNames, key);
        if (generated && await acceptGeneratedFrame(key, expectedNames)) return true;
        const current = currentRenderKey() === key;
        if (generated && current) generationReadyKey = key;
        if (rendered && generated && current) markRenderedKey(key);
        return Boolean(rendered && generated && current && renderedKey === key);
      }).catch(() => false);
      activeRender = { key, promise };
      renderQueue = promise.then(() => {
        if (activeRender?.promise === promise) activeRender = undefined;
      });
      return promise;
    };
    restoreRender = async () => {
      const key = currentRenderKey();
      await requestCurrentRender(key);
      if (currentRenderKey() !== key) return false;
      const api = window.HanossCustomilyCapabilities;
      const expectedNames = crosswordInput()?.value || '';
      if (!crosswordGenerationReady(expectedNames)) return false;
      const items = api.restoreItems(Object.fromEntries(
        [...document.querySelectorAll('.customily_sets-panel [name^="properties["]')]
          .filter((control) => !['radio', 'checkbox'].includes(control.type) || control.checked)
          .map((control) => [control.name.slice(11, -1), control.value])));
      if (!api.canvasTextsMatch(items)) return false;
      // Stored hydration may already contain the exact generated image. A
      // second Create is then a no-op, so frame-difference heuristics cannot
      // establish readiness. Verify provider state and flush its actual canvas.
      await api.getOptionsInstance().engraverDispatcher.commit();
      window.engraver?.canvas?.renderAll();
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      return currentRenderKey() === key && crosswordGenerationReady(expectedNames)
        && api.canvasTextsMatch(items) && markRenderedKey(key);
    };
    const schedulePreGeneration = () => {
      clearTimeout(preGenerateTimer);
      const modal = document.querySelector('.customily-modal-preview-only');
      if (pendingModalKey || (modal && getComputedStyle(modal).display !== 'none')) return;
      preGenerateTimer = setTimeout(() => {
        const names = crosswordInput();
        if (quoteValue() && names?.value.trim() && !invalidCrosswordName()) {
          const key = currentRenderKey();
          requestCurrentRender(key).then((rendered) => {
            if (!rendered && currentRenderKey() === key) setGalleryLoading(false);
          });
        }
      }, 650);
    };
    const handleOptionEdit = (event) => {
      if (!isConfiguredPropertyControl(event.target)) return;
      let quoteBeforeFrame = '';
      const canvas = document.querySelector('.customily-modal-preview-only #preview-canvas');
      try { quoteBeforeFrame = canvas?.width && canvas?.height ? canvas.toDataURL() : ''; } catch { /* Wait for Customily. */ }
      if ((event.isTrusted || cartFieldsResolved) && currentRenderKey() !== observedKey) {
        userEditedOptions = true;
        const canvas = document.querySelector('.customily-modal-preview-only #preview-canvas');
        try { lastOptionEditFrame = canvas?.width && canvas?.height ? canvas.toDataURL() : ''; } catch { lastOptionEditFrame = ''; }
      }
      requestAnimationFrame(() => {
        const key = currentRenderKey();
        if (key === observedKey) return;
        observedKey = key;
        renderedKey = '';
        generationReadyKey = '';
        pendingModalKey = '';
        document.body.classList.remove('hn-ui-render-current');
        const names = crosswordInput();
        clearTimeout(quoteFrameTimer);
        if (!quoteValue()) {
          document.body.classList.remove('hn-ui-quote-gallery');
          setGalleryLoading(false);
        } else if (!names?.value.trim()) {
          setCrosswordVisible(false);
          setGalleryLoading(true);
          showQuoteImageWhenReady(key, quoteBeforeFrame);
        } else if (names?.value.trim()) {
          setCrosswordVisible(true);
          document.body.classList.remove('hn-ui-quote-gallery');
          setGalleryLoading(true);
        }
        schedulePreGeneration();
      });
    };
    document.addEventListener('input', handleOptionEdit, { capture: true });
    document.addEventListener('change', handleOptionEdit, { capture: true });
    document.addEventListener('click', (event) => {
      const candidate = event.target?.closest?.('.customily_sets-panel .csh-dropdown-item');
      const choice = candidate && quoteOption()?.contains(candidate) ? candidate : null;
      const nextQuote = quoteChoiceValue(choice?.textContent.trim());
      if (!nextQuote || nextQuote === quoteValue()) return;
      const canvas = document.querySelector('.customily-modal-preview-only #preview-canvas');
      let before = '';
      try { before = canvas?.width && canvas?.height ? canvas.toDataURL() : ''; } catch { /* Wait for Customily. */ }
      if (event.isTrusted || quoteSelectionFromKeyboard) {
        userEditedOptions = true;
        lastOptionEditFrame = before;
      }
      setGalleryLoading(true);
      requestAnimationFrame(() => {
        if (quoteValue() !== nextQuote) {
          setGalleryLoading(false);
          return;
        }
        const key = currentRenderKey();
        observedKey = key;
        renderedKey = '';
        generationReadyKey = '';
        pendingModalKey = '';
        document.body.classList.remove('hn-ui-render-current');
        const names = crosswordInput();
        if (!names?.value.trim()) showQuoteImageWhenReady(key, before);
        else {
          document.body.classList.remove('hn-ui-quote-gallery');
          schedulePreGeneration();
        }
      });
    }, { capture: true });
    document.addEventListener('click', (event) => {
      const button = event.target?.closest?.('product-form-component #customily-cart-btn');
      if (!button || !crosswordOptionElement()) return;
      const selectedVariant = document.querySelector('body.hn-ui-reference variant-picker input[data-variant-id]:checked')?.dataset.variantId;
      const variantInput = button.closest('product-form-component')?.querySelector('input[name="id"]');
      if (selectedVariant && variantInput && !variantInput.value) variantInput.value = selectedVariant;
      const formVariant = variantInput?.value;
      if (selectedVariant && selectedVariant !== formVariant) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (variantWaitInFlight) return;
        variantWaitInFlight = true;
        button.disabled = true;
        const started = performance.now();
        const waitForVariant = () => {
          const current = document.querySelector('body.hn-ui-reference variant-picker input[data-variant-id]:checked')?.dataset.variantId;
          const freshButton = document.querySelector('product-form-component #customily-cart-btn');
          const ready = current && freshButton?.closest('product-form-component')?.querySelector('input[name="id"]')?.value === current;
          if (ready) {
            variantWaitInFlight = false;
            freshButton.disabled = false;
            freshButton.click();
          } else if (performance.now() - started < 8000) {
            setTimeout(waitForVariant, 75);
          } else {
            variantWaitInFlight = false;
            if (freshButton) { freshButton.disabled = false; freshButton.focus(); }
            setAddPreviewBusy(false);
            showRenderError();
          }
        };
        setTimeout(waitForVariant, 75);
        return;
      }
      const disconnected = invalidCrosswordName();
      if (disconnected) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCrosswordError(disconnected);
        return;
      }
      // Customily owns add-to-cart generation and persistence. A second Create
      // request here made the button wait for a canvas redraw that its current
      // storefront build does not guarantee, before cart/add.js could start.
    }, { capture: true });
    document.addEventListener('submit', (event) => {
      if (!event.target?.closest?.('product-form-component') || !crosswordOptionElement()) return;
      const disconnected = invalidCrosswordName();
      if (!disconnected) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showCrosswordError(disconnected);
    }, { capture: true });
    document.addEventListener('click', (event) => {
      if (!event.target?.closest?.('.customily_sets-panel .customily-download-button')) return;
      const disconnected = invalidCrosswordName();
      if (disconnected) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCrosswordError(disconnected);
        return;
      }
      const canvas = document.querySelector('.customily-modal-preview-only #preview-canvas');
      if (!canvas?.width || !canvas?.height) return;
      const key = currentRenderKey();
      const expectedNames = crosswordInput()?.value || '';
      let before;
      try { before = canvas.toDataURL(); } catch { return; }
      renderPromise = new Promise((resolve) => {
        const started = performance.now();
        const check = () => {
          let changed = false;
          try { changed = canvas.toDataURL() !== before; } catch { /* Wait for the app canvas. */ }
          // Customily may already redraw a new quote with the same crossword
          // names before this click. In that case a repeated Create is a no-op.
          const alreadyCurrent = performance.now() - started >= 450
            && Boolean(crosswordOption())
            && crosswordGenerationReady(expectedNames)
            && currentRenderKey() === key
            && canvas.width > 0 && canvas.height > 0
            && (!lastRenderedCanvas || before !== lastRenderedCanvas)
            && (!userEditedOptions || (lastOptionEditFrame && before !== lastOptionEditFrame));
          if ((changed || alreadyCurrent) && crosswordGenerationReady(expectedNames) && currentRenderKey() === key) {
            const copied = markRenderedKey(key);
            if (copied) {
              try { lastRenderedCanvas = canvas.toDataURL(); } catch { /* Keep the last verified frame. */ }
            }
            resolve(copied);
          } else if (currentRenderKey() !== key && crosswordGenerationSettled(expectedNames)) {
            resolve(false);
          } else if (performance.now() - started < 4000) {
            setTimeout(check, 75);
          } else {
            resolve(false);
          }
        };
        setTimeout(check, 75);
      });
    }, { capture: true });
    document.addEventListener('click', (event) => {
      const button = event.target?.closest?.('.customily-preview-button');
      if (!button) return;
      if (button.dataset.hnUiPreviewRetry) {
        delete button.dataset.hnUiPreviewRetry;
        return;
      }
      const names = crosswordInput();
      const create = document.querySelector('body.hn-ui-reference .customily_sets-panel .customily-download-button');
      const missingQuote = !quoteValue();
      const missingNames = !names?.value.trim();
      if (missingQuote || missingNames) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showMissingPersonalization(missingQuote, missingNames);
        return;
      }
      const disconnected = firstDisconnectedName(names?.value || '');
      if (disconnected) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCrosswordError(disconnected);
        return;
      }
      const key = currentRenderKey();
      if (!create) return;
      if (key !== observedKey) {
        // A Preview click can arrive before the input/change animation frame.
        // Consume that edit now so the later frame cannot clear this modal or
        // schedule a competing Create request after it opens.
        observedKey = key;
        renderedKey = '';
        generationReadyKey = '';
        pendingModalKey = '';
        document.body.classList.remove('hn-ui-render-current');
        clearTimeout(preGenerateTimer);
      }
      if (key === renderedKey && crosswordGenerationReady(names.value) && !activeRender) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      previewButton = button;
      // A queued automatic Create can close a native Preview opened immediately
      // after a relationship switch. Let this click own the next render instead.
      clearTimeout(preGenerateTimer);
      setPreviewBusy(button, true);
      requestCurrentRender(key).then((success) => {
        setPreviewBusy(button, false);
        if (success && currentRenderKey() === key && renderedKey === key && crosswordGenerationReady(names.value)) {
          button.dataset.hnUiPreviewRetry = 'true';
          button.click();
        } else if (currentRenderKey() === key && crosswordGenerationReady(names.value)) {
          // A relationship switch can finish the provider's name generation
          // without painting its hidden canvas until native Preview opens.
          // The modal path verifies a fresh frame before allowing Add.
          openPendingModal(button, key);
        } else if (currentRenderKey() === key) {
          showRenderError();
          button.focus();
        }
      });
    }, { capture: true });
    const openPendingModal = (button, key) => {
      const source = document.querySelector('.customily-modal-preview-only #preview-canvas');
      let openingFrame = '';
      if (source?.width && source?.height) {
        try { openingFrame = source.toDataURL(); } catch { /* Require a new readable frame after opening. */ }
      }
      pendingModalKey = key;
      button.dataset.hnUiPreviewRetry = 'true';
      button.click();
      clearTimeout(modalVerificationTimer);
      const started = performance.now();
      let candidate = '';
      let candidateSince = 0;
      let modalWasVisible = false;
      const check = () => {
        if (pendingModalKey !== key) return;
        const modal = document.querySelector('.customily-modal-preview-only');
        const modalVisible = Boolean(modal && getComputedStyle(modal).display !== 'none');
        if (modalWasVisible && !modalVisible) {
          pendingModalKey = '';
          return;
        }
        modalWasVisible ||= modalVisible;
        const canvas = modal?.querySelector('#preview-canvas');
        const add = modal?.querySelector('.hn-ui-preview__add');
        const names = crosswordInput();
        if (add) {
          add.disabled = true;
          add.setAttribute('aria-busy', 'true');
          add.textContent = 'Preparing design…';
        }
        const current = currentRenderKey() === key && crosswordGenerationReady(names?.value || '');
        let frame = '';
        if (current && modalVisible && canvas?.width && canvas?.height) {
          try { frame = canvas.toDataURL(); } catch { /* Keep Add disabled until the frame can be read. */ }
        }
        const now = performance.now();
        // The provider can finish drawing the newly selected relationship
        // before native Preview opens. Compare with the last verified design
        // as well as the frame captured at modal open in that case.
        const freshFrame = frame && (
          frame !== openingFrame
          || (lastRenderedCanvas && openingFrame !== lastRenderedCanvas && frame !== lastRenderedCanvas)
          || (!lastRenderedCanvas && (!userEditedOptions || (lastOptionEditFrame && frame !== lastOptionEditFrame)))
        );
        if (freshFrame) {
          if (frame !== candidate) {
            candidate = frame;
            candidateSince = now;
          } else if (now - candidateSince >= 450 && now - started >= 600) {
            if (markRenderedKey(key)) {
              lastRenderedCanvas = frame;
              pendingModalKey = '';
              if (add) {
                add.disabled = false;
                add.setAttribute('aria-busy', 'false');
                add.textContent = 'Add to cart';
              }
              return;
            }
          }
        }
        if (currentRenderKey() === key && now - started < 4000) {
          modalVerificationTimer = setTimeout(check, 75);
        } else {
          pendingModalKey = '';
          modal?.querySelector('.customily-close-button')?.click();
          showRenderError();
        }
      };
      modalVerificationTimer = setTimeout(check, 75);
    };
    const restorePreviewFocus = () => {
      if (restoringFocus) return;
      restoringFocus = true;
      const started = performance.now();
      const waitForClose = () => {
        const modal = document.querySelector('.customily-modal-preview-only');
        if (!modal || getComputedStyle(modal).display === 'none') {
          requestAnimationFrame(() => {
            const proxy = document.querySelector('.hn-ui-mobile-preview-proxy[data-hn-ui-preview-invoked]');
            if (proxy) {
              setTimeout(() => {
                window.scrollTo({ top: Number(proxy.dataset.hnUiPreviewScroll || 0), behavior: 'instant' });
                proxy.closest('.hn-ui-mobile-preview-bar')?.classList.add('is-visible');
                proxy.focus({ preventScroll: true });
                proxy.removeAttribute('data-hn-ui-preview-invoked');
                proxy.removeAttribute('data-hn-ui-preview-scroll');
                restoringFocus = false;
              }, 400);
            } else {
              (previewButton || document.querySelector('.customily-preview-button'))?.focus();
              restoringFocus = false;
            }
          });
        } else if (performance.now() - started < 1500) {
          requestAnimationFrame(waitForClose);
        } else {
          restoringFocus = false;
        }
      };
      requestAnimationFrame(waitForClose);
    };
    const decorateModal = () => {
      const modal = document.querySelector('.customily-modal-preview-only');
      const panel = modal?.querySelector('.main');
      const nativeClose = panel?.querySelector('.customily-close-button');
      const canvas = panel?.querySelector('#preview-canvas');
      const wrapper = canvas?.closest('.canvas-wrapper');
      const canvasContainer = canvas?.closest('.canvas-container');
      if (!panel || !nativeClose || !canvas || !wrapper || !canvasContainer) return false;
      if (!nativeClose.dataset.hnUiFocusReady) {
        nativeClose.dataset.hnUiFocusReady = 'true';
        nativeClose.setAttribute('role', 'button');
        nativeClose.setAttribute('tabindex', '0');
        nativeClose.setAttribute('aria-label', 'Close preview');
        nativeClose.addEventListener('click', restorePreviewFocus);
        nativeClose.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          nativeClose.click();
        });
      }
      observer?.disconnect();
      const syncCanvasSize = () => {
        // Customily chooses its source canvas width from the viewport at first
        // render (648px on desktop, sometimes ~351px on a fresh mobile load).
        // Scale that real render to the same visible image width as the reference.
        const sourceWidth = parseFloat(canvasContainer.style.width) || canvas.width;
        const targetWidth = Math.min(486, window.innerWidth - 66);
        if (sourceWidth > 0 && targetWidth > 0) {
          const scale = targetWidth / sourceWidth;
          wrapper.style.zoom = String(scale);
          // The app's internal top inset scales with the source canvas too.
          wrapper.style.marginTop = window.innerWidth <= 749 ? `${40 - 28 * scale}px` : '15px';
        }
      };
      syncCanvasSize();
      if (!panel.dataset.hnUiCanvasReady) {
        panel.dataset.hnUiCanvasReady = 'true';
        new MutationObserver(() => requestAnimationFrame(syncCanvasSize)).observe(canvasContainer, {
          attributes: true, attributeFilter: ['style'],
        });
        window.addEventListener('resize', syncCanvasSize);
      }
      if (panel.querySelector('.hn-ui-preview__header')) {
        const add = panel.querySelector('.hn-ui-preview__add');
        if (add) {
          const names = crosswordInput();
          const ready = !pendingModalKey && renderedKey === currentRenderKey() && crosswordGenerationReady(names?.value || '');
          add.disabled = !ready;
          add.setAttribute('aria-busy', String(!ready));
          add.textContent = ready ? 'Add to cart' : 'Preparing design…';
        }
        panel.querySelector('.hn-ui-preview__close')?.focus();
        return true;
      }

      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'hn-ui-preview-title');
      const header = document.createElement('div');
      header.className = 'hn-ui-preview__header';
      header.innerHTML = '<h2 id="hn-ui-preview-title">Looks Good to Go?</h2><p>Please double-check your preview before adding it to cart.</p>';
      const notes = document.createElement('div');
      notes.className = 'hn-ui-preview__notes';
      notes.innerHTML = '<p>Full refund for faulty production. No question asked</p><p>Your design will be saved with your order.</p>';
      const actions = document.createElement('div');
      actions.className = 'hn-ui-preview__actions';
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'hn-ui-preview__add';
      const names = crosswordInput();
      const readyToAdd = !pendingModalKey && renderedKey === currentRenderKey() && crosswordGenerationReady(names?.value || '');
      add.disabled = !readyToAdd;
      add.setAttribute('aria-busy', String(!readyToAdd));
      add.textContent = readyToAdd ? 'Add to cart' : 'Preparing design…';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'hn-ui-preview__close';
      close.textContent = 'Close';
      const dismiss = () => {
        nativeClose.click();
        restorePreviewFocus();
      };
      close.addEventListener('click', dismiss);
      add.addEventListener('click', () => {
        const names = crosswordInput();
        if (pendingModalKey || renderedKey !== currentRenderKey() || !crosswordGenerationReady(names?.value || '')) return;
        const nativeAdd = document.querySelector('product-form-component #customily-cart-btn');
        if (!nativeAdd || nativeAdd.disabled) return;
        nativeClose.click();
        requestAnimationFrame(() => nativeAdd.click());
      });
      actions.append(add, close);
      panel.insertBefore(header, nativeClose);
      panel.append(notes, actions);
      close.focus();
      return true;
    };
    document.addEventListener('click', (event) => {
      previewButton = event.target?.closest?.('.customily-preview-button');
      if (!previewButton) return;
      if (decorateModal()) {
        requestAnimationFrame(() => document.querySelector('.customily-modal-preview-only .hn-ui-preview__close')?.focus());
        return;
      }
      observer?.disconnect();
      observer = window.HanossCustomilyCapabilities?.watchPreview(decorateModal);
    }, { capture: true });
    document.addEventListener('click', (event) => {
      const modal = event.target;
      if (!modal?.matches?.('.customily-modal-preview-only') || !modal.querySelector('.hn-ui-preview__header')) return;
      // Customily owns backdrop dismissal; restore focus after its handler hides it.
      restorePreviewFocus();
    }, { capture: true });
    document.addEventListener('keydown', (event) => {
      const modal = document.querySelector('.customily-modal-preview-only');
      if (!modal?.querySelector('.hn-ui-preview__header') || getComputedStyle(modal).display === 'none') return;
      if (event.key === 'Tab') {
        const add = modal.querySelector('.hn-ui-preview__add');
        const close = modal.querySelector('.hn-ui-preview__close');
        const nativeClose = modal.querySelector('.customily-close-button');
        const controls = [nativeClose, add, close].filter(Boolean);
        if (!add || !close || !controls.length) return;
        event.preventDefault();
        const index = controls.indexOf(document.activeElement);
        controls[(index + (event.shiftKey ? controls.length - 1 : 1)) % controls.length].focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        modal.querySelector('.customily-close-button')?.click();
        restorePreviewFocus();
      }
    }, { capture: true });
  };
  const setupMobilePreviewBar = () => {
    if (document.body.dataset.hnUiMobilePreviewReady) return;
    document.body.dataset.hnUiMobilePreviewReady = 'true';
    const bar = document.createElement('div');
    bar.className = 'hn-ui-mobile-preview-bar';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hn-ui-mobile-preview-proxy';
    button.textContent = 'Preview';
    bar.append(button);
    document.body.append(bar);
    let inlineButton;
    let intersecting;
    const visibility = new IntersectionObserver((entries) => {
      const entry = entries.find((item) => item.target === inlineButton);
      if (!entry) return;
      intersecting = entry.boundingClientRect.top <= innerHeight && entry.boundingClientRect.bottom >= 0;
      sync();
    });
    const sync = () => {
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const next = document.querySelector('.product-form-buttons .customily-preview-button');
      if (next !== inlineButton) {
        if (inlineButton) visibility.unobserve(inlineButton);
        inlineButton = next;
        intersecting = undefined;
        if (inlineButton) visibility.observe(inlineButton);
      }
      bar.classList.toggle('is-visible', Boolean(mobile && inlineButton && intersecting === false));
    };
    button.addEventListener('click', () => {
      if (!inlineButton) return;
      button.dataset.hnUiPreviewInvoked = 'true';
      button.dataset.hnUiPreviewScroll = String(window.scrollY);
      inlineButton.click();
    });
    window.addEventListener('resize', sync, { passive: true });
    window.HanossCustomilyCapabilities?.onPanelChange(sync);
    const actions = document.querySelector('.product-form-buttons');
    if (actions) {
      let syncFrame;
      new MutationObserver(() => {
        if (syncFrame) return;
        syncFrame = requestAnimationFrame(() => { syncFrame = null; sync(); });
      }).observe(actions, { childList: true, subtree: true });
    }
    sync();
  };
  let mounted = false;
  let mountObserver;

  const hasConfiguredComposition = () => Boolean(
    quoteOption()
    && crosswordOptionElement()
    && document.querySelector('.customily_sets-panel .customily-download-button')
    && document.querySelector('.product-form-buttons .customily-preview-button')
  );

  const mount = () => {
    if (mounted) return true;
    if (!hasConfiguredComposition()) return false;
    if (document.body.dataset.hnUiConfirmationReady
      && document.body.dataset.hnCustomilyCrosswordPreviewOwner !== 'shared') return false;

    mounted = true;
    mountObserver?.();
    document.body.dataset.hnCustomilyCrosswordPreviewOwner = 'shared';
    setupLiveGallery();
    watchLiveGallery();
    setupPreviewConfirmation();
    setupMobilePreviewBar();
    window.addEventListener('resize', syncGalleryMode, { passive: true });
    syncGalleryMode();
    return true;
  };

  window.HanossCustomilyCrosswordPreview = Object.freeze({
    requestCurrentRender: () => restoreRender?.(),
    mount,
    get mounted() { return mounted; },
  });

  const init = () => {
    const ready = mount();
    document.addEventListener('hanoss:customily-capabilities', mount);
    if (!ready) mountObserver = window.HanossCustomilyCapabilities?.onPanelChange(mount);
    document.addEventListener('shopify:section:load', () => {
      if (mounted) { setupLiveGallery(); watchLiveGallery(); }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
